import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import CinemaIcon from "../cinema/CinemaIcon";
import logo from "../../assets/logo/atlas-logo.png";
import useConnectionStatus from "../../utils/useConnectionStatus";
import { getRecoveryPath, getStatusCopy } from "./statusCopy";
import LoadingView from "./LoadingView";
import "./StatusView.scss";

export default function StatusView({
  kind = "loading",
  size = "page",
  variant,
  title,
  description,
  onRetry,
  error,
  onHome,
  homeTo = getRecoveryPath()
}) {
  const copy = getStatusCopy();
  const online = useConnectionStatus();
  const networkMessage = kind === "error" && onRetry && !online;
  const timedOut = kind === "error" && error?.code === "LOAD_TIMEOUT";
  const loading = kind === "loading";
  const notFound = kind === "notFound";
  const Heading = size === "page" ? "h1" : "h2";
  const homeLabel = homeTo === "/login" ? copy.login : copy.home;

  useEffect(() => {
    if (size === "page" || notFound) window.scrollTo(0, 0);
  }, [size, notFound]);

  if (loading) {
    return (
      <LoadingView
        size={size}
        variant={variant}
        title={title}
        description={description}
      />
    );
  }

  return (
    <section
      className={`atlas-status atlas-status--${kind} atlas-status--${size}`}
    >
      {size === "page" && (
        <Link
          className="atlas-status-brand"
          to={homeTo}
          aria-label="Atlas"
          onClick={
            onHome
              ? (event) => {
                  event.preventDefault();
                  onHome();
                }
              : undefined
          }
        >
          <img src={logo} alt="" />
        </Link>
      )}
      <div className="atlas-status-content">
        <div className="atlas-status-art" aria-hidden="true">
          <span className="atlas-status-orbit" />
          <span className="atlas-status-orbit atlas-status-orbit--inner" />
          {notFound ? (
            <span className="atlas-status-code">404</span>
          ) : (
            <span className="atlas-status-symbol">
              <CinemaIcon name={loading ? "play" : "pause"} />
            </span>
          )}
          <span className="atlas-status-star" />
        </div>
        <div
          role={loading ? "status" : "alert"}
          aria-live={loading ? "polite" : "assertive"}
          aria-atomic="true"
        >
          <p className="atlas-status-eyebrow">{copy[`${kind}Eyebrow`]}</p>
          <Heading>
            {networkMessage
              ? copy.offlineTitle
              : title || (timedOut ? copy.timeoutTitle : copy[`${kind}Title`])}
          </Heading>
          <p className="atlas-status-description">
            {networkMessage
              ? copy.offlineNote
              : description ||
                (timedOut ? copy.timeoutNote : copy[`${kind}Note`])}
          </p>
          {loading && (
            <span className="atlas-status-sr">{copy.loadingLabel}</span>
          )}
        </div>
        {loading ? (
          <div className="atlas-status-progress" aria-hidden="true">
            <span />
          </div>
        ) : (
          <div className="atlas-status-actions">
            {onRetry && (
              <button
                className="atlas-status-button atlas-status-button--primary"
                type="button"
                onClick={onRetry}
              >
                <CinemaIcon name="history" />
                {copy.retry}
              </button>
            )}
            {onHome ? (
              <button
                className="atlas-status-button"
                type="button"
                onClick={onHome}
              >
                {homeLabel}
                <CinemaIcon name="arrow" />
              </button>
            ) : (
              <Link
                className={`atlas-status-button ${
                  notFound ? "atlas-status-button--primary" : ""
                }`}
                to={homeTo}
              >
                {homeLabel}
                <CinemaIcon name="arrow" />
              </Link>
            )}
          </div>
        )}
      </div>
      {size === "page" && (
        <p className="atlas-status-signature">{copy.signature}</p>
      )}
    </section>
  );
}

export const LoadingState = (props) => <StatusView {...props} kind="loading" />;
export const ErrorState = (props) => <StatusView {...props} kind="error" />;
export const NotFoundPage = (props) => (
  <StatusView {...props} kind="notFound" />
);
